import axios from 'axios';
import { GasInfo, GenesisRotationDCARequest, GenesisPipelineStartResponse, GenesisPipelineStatus, GenesisPipelineStatistics, GenesisPipelineListResponse, GenesisAddWorkersResponse, GenesisActivePipelinesResponse, V4PoolDiscoveryResponse, DexType } from '../types';

// Базовый URL для API (можно изменить через переменные окружения)
const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://base.dca.ink';

const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 15000,
  headers: {
    'Content-Type': 'application/json',
  },
  maxRedirects: 5,
  validateStatus: function (status) {
    return status >= 200 && status < 300;
  },
});

// Auth interceptor — добавляет JWT токен к каждому запросу
api.interceptors.request.use(config => {
  const token = sessionStorage.getItem('auth_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Response interceptor — при 401 разлогинивает
api.interceptors.response.use(
  response => response,
  error => {
    if (error.response?.status === 401) {
      sessionStorage.removeItem('auth_token');
      window.location.reload();
    }
    return Promise.reject(error);
  }
);

// Типы данных для нового API
export interface GlobalConfig {
  rpc_url: string;
  token_address: string;
  network: string;
  quote_token_address: string;
}

export interface DCARequest {
  private_key: string;
  action: 'buy' | 'sell';
  min_amount: number;
  max_amount: number;
  interval_seconds: number;
  total_transactions: number;
  slippage: number;
  max_gas_usd: number;
  // new required fields in backend request
  rpc_url: string;
  token_address: string;
  network: string; // default to 'base'
  quote_token_address: string;
  // DEX выбор (опционально, default backend = kyberswap)
  dex_type?: DexType;
  pool_id?: string;
  hook_address?: string;
}

export interface MarketCapDCARequest {
  private_key: string;
  action: 'buy' | 'sell';
  check_interval_seconds: number;
  min_market_cap: number;
  max_market_cap: number;
  min_amount: number;
  max_amount: number;
  trade_interval_seconds: number;
  slippage: number;
  max_gas_usd: number;
}

export interface DCAStatus {
  task_id: string;
  wallet_address: string;
  action: string;
  min_amount: number;
  max_amount: number;
  interval_seconds: number;
  total_transactions: number;
  executed_transactions: number;
  slippage: number;
  max_gas_usd: number;
  status: string;
  started_at: string;
  operations_count: number;
  last_operation?: string;
  last_error?: string;
  error_code?: string;
  error_details?: any;
  paused_reason?: string;
}

export interface ErrorCodes {
  error_codes: Record<string, string>;
  task_statuses: Record<string, string>;
  status_behavior: Record<string, string>;
}

export interface ConfigResponse {
  message: string;
  config: GlobalConfig;
}

export interface WalletBalance {
  wallet_address: string;
  eth_balance: number;
  token?: {
    address: string;
    symbol: string;
    balance: number;
  };
}

export interface PriceData {
  eth_price_usd: number;
  token_price_usd: number;
  token_address: string;
  timestamp: string;
}

// API методы для нового API
export const apiService = {
  // Авторизация
  async login(password: string): Promise<{ token: string }> {
    const response = await api.post('/auth/login', { password });
    return response.data;
  },

  // Баланс кошелька
  async getBalance(privateKey: string, rpcUrl: string, tokenAddress: string): Promise<WalletBalance> {
    // retry up to 3 times with increasing timeout
    const tries = [8000, 12000, 15000];
    let lastError: any;
    for (let i = 0; i < tries.length; i++) {
      try {
        const params = new URLSearchParams({
          private_key: privateKey,
          rpc_url: rpcUrl,
          token_address: tokenAddress,
        });
        const response = await api.get(`/balance?${params.toString()}`, {
          timeout: tries[i],
        });
        return response.data;
      } catch (err: any) {
        lastError = err;
        // Если это не таймаут или последняя попытка, не продолжаем
        if (err.code !== 'ECONNABORTED' && err.response?.status !== 408) {
          throw err;
        }
        if (i === tries.length - 1) {
          throw err;
        }
        // Небольшая задержка между попытками
        await new Promise(resolve => setTimeout(resolve, 500));
      }
    }
    throw lastError;
  },

  // Баланс по публичному адресу (без приватного ключа)
  async getBalanceByAddress(walletAddress: string, rpcUrl: string, tokenAddress: string): Promise<WalletBalance> {
    const params = new URLSearchParams({
      wallet_address: walletAddress,
      rpc_url: rpcUrl,
      token_address: tokenAddress,
    });
    const response = await api.get(`/balance-by-address?${params.toString()}`, { timeout: 10000 });
    return response.data;
  },

  // DCA задачи
  async startDCA(request: DCARequest): Promise<{ task_id: string; message: string }> {
    const response = await api.post('/dca/start', request);
    return response.data;
  },

  async startMarketCapDCA(request: MarketCapDCARequest): Promise<{ task_id: string; message: string }> {
    const response = await api.post('/dca/start-marketcap', request);
    return response.data;
  },

  async stopDCA(taskId: string): Promise<{ message: string; task_id: string }> {
    const response = await api.post(`/dca/stop/${taskId}`);
    return response.data;
  },

  async stopAllDCA(): Promise<{ message: string; total_stopped: number }> {
    const response = await api.post('/dca/stop/all');
    return response.data;
  },

  async getTasks(): Promise<DCAStatus[]> {
    const response = await api.get('/dca/tasks');
    return response.data;
  },

  async getTask(taskId: string): Promise<DCAStatus> {
    const response = await api.get(`/dca/task/${taskId}`);
    return response.data;
  },

  async deleteTask(taskId: string): Promise<{ message: string }> {
    const response = await api.delete(`/dca/task/${taskId}`);
    return response.data;
  },

  async getErrorCodes(): Promise<ErrorCodes> {
    const response = await api.get('/dca/error-codes');
    return response.data;
  },

  // Дополнительные методы (если нужны)
  async getGasInfo(rpcUrl?: string, network: string = 'base'): Promise<GasInfo> {
    // retry с меньшими таймаутами
    const tries = [8000, 12000, 15000];
    let lastError: any;
    for (let i = 0; i < tries.length; i++) {
      try {
        const urlSearch = new URLSearchParams({
          rpc_url: rpcUrl || 'https://mainnet.base.org',
          network: network || 'base',
        });
        const response = await api.get(`/check_gas?${urlSearch.toString()}`, {
          timeout: tries[i],
        });
        return response.data;
      } catch (err: any) {
        lastError = err;
        // Если это не таймаут или последняя попытка, не продолжаем
        if (err.code !== 'ECONNABORTED' && err.response?.status !== 408) {
          throw err;
        }
        if (i === tries.length - 1) {
          throw err;
        }
        // Небольшая задержка между попытками
        await new Promise(resolve => setTimeout(resolve, 500));
      }
    }
    throw lastError;
  },

  async getPrices(tokenAddress?: string, network: string = 'base'): Promise<PriceData> {
    // retry с меньшими таймаутами
    const tries = [8000, 12000, 15000];
    let lastError: any;
    for (let i = 0; i < tries.length; i++) {
      try {
        const params = new URLSearchParams({
          token_address: tokenAddress || '0x0000000000000000000000000000000000000000',
          network: network || 'base',
        });
        const response = await api.get(`/prices?${params.toString()}`, {
          timeout: tries[i],
        });
        return response.data;
      } catch (err: any) {
        lastError = err;
        // Если это не таймаут или последняя попытка, не продолжаем
        if (err.code !== 'ECONNABORTED' && err.response?.status !== 408) {
          throw err;
        }
        if (i === tries.length - 1) {
          throw err;
        }
        // Небольшая задержка между попытками
        await new Promise(resolve => setTimeout(resolve, 500));
      }
    }
    throw lastError;
  },

  // Discover Uniswap V3/V4 pools для (token, quote_token) через DexScreener + on-chain
  async discoverV4Pools(
    tokenAddress: string,
    quoteTokenAddress?: string,
    network: string = 'base',
    dexType?: 'uniswap-v3' | 'uniswap-v4'
  ): Promise<V4PoolDiscoveryResponse> {
    const params = new URLSearchParams({
      token_address: tokenAddress,
      network,
    });
    if (quoteTokenAddress) params.append('quote_token_address', quoteTokenAddress);
    if (dexType) params.append('dex_type', dexType);
    const response = await api.get(`/pools/discover?${params.toString()}`, {
      timeout: 30000, // discovery может занять до 20+ секунд для V4 (log scan)
    });
    return response.data;
  },

  // Mass transfer
  async massTransfer(request: {
    tokenMint?: string;
    fromAddresses: string[];
    toAddresses: string[];
    evenDistribution: boolean;
    rpc_url?: string;
    leaveTokens?: boolean;
    leaveTokensMin?: number | null;
    leaveTokensMax?: number | null;
  }): Promise<{
    message: string;
    mode: string;
    network: string;
    from_count?: number;
    to_count?: number;
    pairs_count?: number;
    token_address?: string;
  }> {
    const response = await api.post('/mass-transfer', request, {
      timeout: 300000, // 5 минут для массового трансфера
    });
    return response.data;
  },

  // ========== Genesis DCA ==========

  async startGenesisDCA(request: GenesisRotationDCARequest): Promise<GenesisPipelineStartResponse> {
    const response = await api.post('/base-genesis-dca/start', request);
    return response.data;
  },

  async stopGenesisPipeline(pipelineId: string): Promise<{ message: string; pipeline_id: string }> {
    const response = await api.post(`/base-genesis-dca/stop/${pipelineId}`);
    return response.data;
  },

  async getGenesisPipelineStatus(pipelineId: string): Promise<GenesisPipelineStatus> {
    const response = await api.get(`/base-genesis-dca/status/${pipelineId}`);
    return response.data;
  },

  async getGenesisPipelineStatistics(pipelineId: string): Promise<GenesisPipelineStatistics> {
    const response = await api.get(`/base-genesis-dca/statistics/${pipelineId}`);
    return response.data;
  },

  async listGenesisPipelines(tokenAddress?: string): Promise<GenesisPipelineListResponse> {
    const params = tokenAddress ? `?token_address=${tokenAddress}` : '';
    const response = await api.get(`/base-genesis-dca/list${params}`);
    return response.data;
  },

  async getGenesisActivePipelines(): Promise<GenesisActivePipelinesResponse> {
    const response = await api.get('/base-genesis-dca/active');
    return response.data;
  },

  async addGenesisWorkers(pipelineId: string, workerPrivateKeys: string[]): Promise<GenesisAddWorkersResponse> {
    const response = await api.post(`/base-genesis-dca/${pipelineId}/add-workers`, {
      worker_private_keys: workerPrivateKeys,
    });
    return response.data;
  },

  async getGenesisPipelineFullData(pipelineId: string): Promise<any> {
    const response = await api.get(`/base-genesis-dca/get/${pipelineId}`);
    return response.data;
  },

  async getGenesisDbStatus(): Promise<{ status: string; postgresql_version?: string; pipelines_count?: number; error?: string }> {
    const response = await api.get('/base-genesis-dca/db/status');
    return response.data;
  },
};
export default apiService;
